import * as IoC from 'typescript-ioc';
import * as React from 'react';
import { IEventBlueprintTriggerJobDetails, IEventBlueprintTriggerJobResults, IJobDescriptionDTO, IJobsDAO, JobDetailsTypes, JobResultTypes, JobStatus, JobType } from '../../api/jobs.dao';
import { IAlertsService } from '../../infrastructure/alerts';
import { Component } from '../../infrastructure/type-extensions';
import { IJobsMonitor } from '../../domain/jobs-monitor/jobs-monitor';
import { JobDtoType } from '../../domain/jobs';
import { JobTrackingService } from '../../domain/jobs/implementations/job-tracking.service';
import { EventExecutionRuntime } from '../../domain/jobs/implementations/event-execution-runtime';

interface IJobResultComponentProps<T extends JobDetailsTypes, K extends JobResultTypes = null> {
    jobDetails: T;
    result?: K;
}

interface IConcreteJobConfirmationPopupProperties<P extends IJobResultComponentProps<JobDetailsTypes, JobResultTypes>> {
    completeComponent: Component<P>;
    failedComponent: Component<P>;
    reviewCompletedJobButtonText: string;
    reviewFailedJobButtonText: string;
}

class JobsMonitorHostVM {
    private shownAlertsNumber: number;
    private combinedEventAlertShown: boolean = false;
    private combinedEventFailureShown: boolean = false;
    private eventBlueprintJobId: number | null = null;
    private meetingBlueprintJobId: number | null = null;
    private reviewedJobIds: Set<number> = new Set();
    private pendingReviewJobIds: Set<number> = new Set();
    private shownBlueprintJobIds: Set<number> = new Set();

    constructor(
        private readonly registeredConfirmationPopups: {
            [x in JobType]?: IConcreteJobConfirmationPopupProperties<IJobResultComponentProps<JobDetailsTypes, JobResultTypes>>
        },
        private readonly jobsMonitor: IJobsMonitor = IoC.Container.get(IJobsMonitor),
        private readonly jobsDao: IJobsDAO = IoC.Container.get(IJobsDAO),
        private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        private readonly jobTrackingService: JobTrackingService = IoC.Container.get(JobTrackingService),
        private readonly executionRuntime: EventExecutionRuntime = IoC.Container.get(EventExecutionRuntime)
    ) {
        this.shownAlertsNumber = 0;
    }

    public initialize() {
        this.jobsMonitor.initialize();
        for (const jobType of Object.keys(this.registeredConfirmationPopups)) {
            this.jobsMonitor.registerHandler(jobType as JobType, this.showConfirmationPopupHandler);
        }
    }

    public cleanup() {
        this.jobsMonitor.cleanup();
    }

    private getEventRegisteredPopupProperties =
        (): IConcreteJobConfirmationPopupProperties<IJobResultComponentProps<IEventBlueprintTriggerJobDetails, IEventBlueprintTriggerJobResults>> => {
            // eslint-disable-next-line semi, max-len
            return this.registeredConfirmationPopups[JobType.EventBlueprintTrigger] as IConcreteJobConfirmationPopupProperties<IJobResultComponentProps<IEventBlueprintTriggerJobDetails, IEventBlueprintTriggerJobResults>>;
        };

    private isBlueprintJob(job: IJobDescriptionDTO<JobDetailsTypes, JobResultTypes> | JobDtoType): boolean {
        return job.jobType === JobType.EventBlueprintTrigger || job.jobType === JobType.MeetingBlueprintTrigger;
    }

    private shouldShowBlueprintJobPopup(job: IJobDescriptionDTO<JobDetailsTypes, JobResultTypes>): boolean {
        if (!this.isBlueprintJob(job)) return true;

        const executionContext = this.executionRuntime.getExecutionContext();
        if (!executionContext.isValid()) return false;

        const jobDetails = job.jobDetails as { accountId: number; eventId: number };
        const currentContext = executionContext.getContextParams();

        return jobDetails.accountId === currentContext.accountId && jobDetails.eventId === currentContext.eventId;
    }

    private showConfirmationPopupHandler = <T extends JobDetailsTypes, K extends JobResultTypes>(job: IJobDescriptionDTO<T, K>) => {
        this.cleanupReviewedJobIds();
        if (this.reviewedJobIds.has(job.jobId) || this.pendingReviewJobIds.has(job.jobId)) return;

        if (!this.shouldShowBlueprintJobPopup(job)) return;

        if (job.jobType === JobType.MeetingBlueprintTrigger) {
            const blueprintStatus = this.getBlueprintJobsStatus(job);
            const eventJob = blueprintStatus.eventJob;
            if (blueprintStatus.eventExists && eventJob && this.doJobsBelongToSameEvent(job, eventJob)) {
                if (this.shouldShowCombinedSuccessAlert(eventJob, job)) {
                    this.showCombinedSuccessAlert(eventJob, job);
                    return;
                }

                if (job.jobStatus === JobStatus.Completed && eventJob.jobStatus === JobStatus.Failed && !this.combinedEventFailureShown) {
                    this.showCombinedFailureAlert(eventJob, job);
                    return;
                }

                if (this.shouldShowCombinedFailureAlert(eventJob, job)) {
                    this.showCombinedFailureAlert(eventJob, job);
                    return;
                }
                return;
            }
        }

        if (this.shownAlertsNumber > 0 && !this.combinedEventAlertShown && !this.combinedEventFailureShown) {
            return;
        }

        const registeredPopupProperties = (
            this.registeredConfirmationPopups[job.jobType] as IConcreteJobConfirmationPopupProperties<IJobResultComponentProps<T, K>>
        );
        if (job.jobStatus === JobStatus.Completed) {
            if (job.jobType === JobType.EventBlueprintTrigger) {
                const blueprintStatus = this.getBlueprintJobsStatus(job);
                const meetingJob = blueprintStatus.meetingJob;
                if (blueprintStatus.eventExists && meetingJob && this.doJobsBelongToSameEvent(job, meetingJob)) {
                    if (this.shouldShowCombinedSuccessAlert(job, meetingJob)) {
                        this.cleanupIndividuallyShownJob(meetingJob.jobId);
                        this.setCombinedAlertState(job.jobId, meetingJob.jobId, true);
                        this.showCombinedAlert(job, true, meetingJob);
                        return;
                    }

                    if (job.jobStatus === JobStatus.Completed && meetingJob.jobStatus === JobStatus.Failed && !this.combinedEventFailureShown) {
                        this.showCombinedFailureAlert(job, meetingJob);
                        return;
                    }
                    return;
                }
            }
            this.shownAlertsNumber++;
            if (this.isBlueprintJob(job)) {
                this.shownBlueprintJobIds.add(job.jobId);
            }
            if (job.jobType === JobType.DeleteEvent) {
                this.handleReviewButton(job);
            } else {
                this.alertsService.alert({
                    renderCrossIcon: false,
                    okButtonProps: {
                        text: registeredPopupProperties.reviewCompletedJobButtonText || 'Ok',
                        handler: () => this.handleReviewButton(job),
                    },
                    message: React.createElement(registeredPopupProperties.completeComponent, {
                        jobDetails: job.jobDetails,
                        result: job.result
                    }),
                });
            }
        }

        if (job.jobStatus === JobStatus.Failed) {
            if (job.jobType === JobType.EventBlueprintTrigger) {
                const blueprintStatus = this.getBlueprintJobsStatus(job);
                const meetingJob = blueprintStatus.meetingJob;
                if (blueprintStatus.eventExists && meetingJob && this.doJobsBelongToSameEvent(job, meetingJob)) {
                    if (this.shouldShowCombinedFailureAlert(job, meetingJob)) {
                        this.cleanupIndividuallyShownJob(meetingJob.jobId);
                        this.setCombinedAlertState(job.jobId, meetingJob.jobId, false);
                        this.showCombinedAlert(job, false, meetingJob);
                        return;
                    }
                    return;
                }
            }
            this.shownAlertsNumber++;
            if (this.isBlueprintJob(job)) {
                this.shownBlueprintJobIds.add(job.jobId);
            }
            this.alertsService.alert({
                renderCrossIcon: false,
                okButtonProps: {
                    text: registeredPopupProperties.reviewFailedJobButtonText || 'Ok',
                    handler: () => this.handleReviewButton(job),
                },
                message: React.createElement(registeredPopupProperties.failedComponent, {
                    jobDetails: job.jobDetails,
                    result: job.result
                }),
            });
        }
    };

    private handleReviewButton = async (job: JobDtoType) => {
        try {
            if (this.eventBlueprintJobId !== null && this.meetingBlueprintJobId !== null) {
                this.pendingReviewJobIds.add(this.eventBlueprintJobId);
                this.pendingReviewJobIds.add(this.meetingBlueprintJobId);
                await Promise.all([
                    this.jobsDao.markJobAsReviewed(this.eventBlueprintJobId),
                    this.jobsDao.markJobAsReviewed(this.meetingBlueprintJobId)
                ]);

                const allEventJobs = this.jobsMonitor.getJobsByTypeFromCache(JobType.EventBlueprintTrigger);
                const allMeetingJobs = this.jobsMonitor.getJobsByTypeFromCache(JobType.MeetingBlueprintTrigger);
                const referenceEventJob = allEventJobs.find(j => j.jobId === this.eventBlueprintJobId);
                const referenceJob = referenceEventJob || allMeetingJobs.find(j => j.jobId === this.meetingBlueprintJobId);

                const blueprintStatus = this.getBlueprintJobsStatus(referenceJob || undefined);
                if (blueprintStatus.eventJob) {
                    this.jobTrackingService.trackJobExecutionResult(blueprintStatus.eventJob);
                }
                if (blueprintStatus.meetingJob) {
                    this.jobTrackingService.trackJobExecutionResult(blueprintStatus.meetingJob);
                }

                if (blueprintStatus.eventJob || blueprintStatus.meetingJob) {
                    await this.executionRuntime.triggerHandlersByType(JobType.EventBlueprintTrigger);
                    await this.executionRuntime.triggerHandlersByType(JobType.MeetingBlueprintTrigger);
                }
                const eventJobId = this.eventBlueprintJobId;
                const meetingJobId = this.meetingBlueprintJobId;

                this.pendingReviewJobIds.delete(eventJobId);
                this.pendingReviewJobIds.delete(meetingJobId);
                this.reviewedJobIds.add(eventJobId);
                this.reviewedJobIds.add(meetingJobId);
                this.shownBlueprintJobIds.delete(eventJobId);
                this.shownBlueprintJobIds.delete(meetingJobId);

                this.combinedEventAlertShown = false;
                this.combinedEventFailureShown = false;
                this.eventBlueprintJobId = null;
                this.meetingBlueprintJobId = null;
            } else {
                await this.jobsDao.markJobAsReviewed(job.jobId);
                this.jobTrackingService.trackJobExecutionResult(job);
                if (this.isBlueprintJob(job)) {
                    this.shownBlueprintJobIds.delete(job.jobId);
                }
            }
        } catch (e) {
            console.error('Failed to mark job as reviewed:', e);
            if (this.eventBlueprintJobId !== null) {
                this.pendingReviewJobIds.delete(this.eventBlueprintJobId);
            }
            if (this.meetingBlueprintJobId !== null) {
                this.pendingReviewJobIds.delete(this.meetingBlueprintJobId);
            }
        } finally {
            if (this.shownAlertsNumber > 0) {
                this.shownAlertsNumber--;
            }
        }
    };

    private areBothJobsFinalized(eventJob: JobDtoType | null, meetingJob: JobDtoType | null): boolean {
        const eventFinalized = eventJob?.jobStatus === JobStatus.Failed || eventJob?.jobStatus === JobStatus.Completed;
        const meetingFinalized = meetingJob?.jobStatus === JobStatus.Failed || meetingJob?.jobStatus === JobStatus.Completed;
        return eventFinalized && meetingFinalized;
    }

    private doJobsBelongToSameEvent(job1: JobDtoType, job2: JobDtoType): boolean {
        const job1Details = job1.jobDetails as { accountId: number; eventId: number };
        const job2Details = job2.jobDetails as { accountId: number; eventId: number };
        return job1Details.accountId === job2Details.accountId && job1Details.eventId === job2Details.eventId;
    }
    private cleanupIndividuallyShownJob(jobId: number): void {
        if (this.shownBlueprintJobIds.has(jobId)) {
            this.shownBlueprintJobIds.delete(jobId);
            if (this.shownAlertsNumber > 0) {
                this.shownAlertsNumber--;
            }
        }
    }
    private showCombinedSuccessAlert(eventJob: JobDtoType, meetingJob: JobDtoType): void {
        this.cleanupIndividuallyShownJob(eventJob.jobId);
        this.setCombinedAlertState(eventJob.jobId, meetingJob.jobId, true);
        this.showCombinedAlert(eventJob, true, meetingJob);
    }

    private showCombinedFailureAlert(eventJob: JobDtoType, meetingJob: JobDtoType): void {
        this.cleanupIndividuallyShownJob(eventJob.jobId);
        this.setCombinedAlertState(eventJob.jobId, meetingJob.jobId, false);
        this.showCombinedAlert(eventJob, false, meetingJob);
    }

    private shouldShowCombinedSuccessAlert(eventJob: JobDtoType, meetingJob: JobDtoType): boolean {
        return eventJob.jobStatus === JobStatus.Completed
            && meetingJob.jobStatus === JobStatus.Completed
            && !this.combinedEventAlertShown;
    }

    private shouldShowCombinedFailureAlert(eventJob: JobDtoType, meetingJob: JobDtoType): boolean {
        const bothFinalized = this.areBothJobsFinalized(eventJob, meetingJob);
        const hasFailure = eventJob.jobStatus === JobStatus.Failed || meetingJob.jobStatus === JobStatus.Failed;
        return bothFinalized && hasFailure && !this.combinedEventFailureShown;
    }

    private getBlueprintJobsStatus(referenceJob?: JobDtoType) {
        if (!this.jobsMonitor.cacheExists()) {
            return { eventExists: false, meetingExists: false, eventJob: null, meetingJob: null };
        }

        const eventJobs = this.jobsMonitor.getJobsByTypeFromCache(JobType.EventBlueprintTrigger);
        const meetingJobs = this.jobsMonitor.getJobsByTypeFromCache(JobType.MeetingBlueprintTrigger);

        let eventJob: JobDtoType | null = null;
        let meetingJob: JobDtoType | null = null;
        if (referenceJob && this.isBlueprintJob(referenceJob)) {
            const referenceAccountId = (referenceJob.jobDetails as IEventBlueprintTriggerJobDetails | { accountId: number; eventId: number }).accountId;
            const referenceEventId = (referenceJob.jobDetails as IEventBlueprintTriggerJobDetails | { accountId: number; eventId: number }).eventId;

            eventJob = eventJobs.find(job => {
                const jobDetails = job.jobDetails as IEventBlueprintTriggerJobDetails;
                return jobDetails.accountId === referenceAccountId && jobDetails.eventId === referenceEventId;
            }) || null;

            meetingJob = meetingJobs.find(job => {
                const jobDetails = job.jobDetails as { accountId: number; eventId: number };
                return jobDetails.accountId === referenceAccountId && jobDetails.eventId === referenceEventId;
            }) || null;
        } else {
            eventJob = eventJobs.length > 0 ? eventJobs[0] : null;
            meetingJob = meetingJobs.length > 0 ? meetingJobs[0] : null;
        }

        return {
            eventExists: eventJob !== null,
            meetingExists: meetingJob !== null,
            eventJob: eventJob,
            meetingJob: meetingJob
        };
    }

    private cleanupReviewedJobIds(): void {
        if (!this.jobsMonitor.cacheExists()) {
            return;
        }
        const activeJobIds = new Set(
            this.jobsMonitor.getJobsByTypeFromCache(JobType.EventBlueprintTrigger)
                .concat(this.jobsMonitor.getJobsByTypeFromCache(JobType.MeetingBlueprintTrigger))
                .map(job => job.jobId)
        );
        this.reviewedJobIds.forEach(jobId => {
            if (!activeJobIds.has(jobId)) {
                this.reviewedJobIds.delete(jobId);
            }
        });
    }

    private showCombinedAlert(
        eventJob: IJobDescriptionDTO<IEventBlueprintTriggerJobDetails, IEventBlueprintTriggerJobResults> | JobDtoType,
        isSuccess: boolean,
        handlerJob: JobDtoType
    ): void {
        const eventRegisteredPopupProperties = this.getEventRegisteredPopupProperties();
        const eventJobTyped = eventJob as IJobDescriptionDTO<IEventBlueprintTriggerJobDetails, IEventBlueprintTriggerJobResults>;

        this.alertsService.alert({
            renderCrossIcon: false,
            okButtonProps: {
                text: isSuccess
                    ? eventRegisteredPopupProperties.reviewCompletedJobButtonText || 'Ok'
                    : eventRegisteredPopupProperties.reviewFailedJobButtonText || 'Ok',
                handler: () => this.handleReviewButton(handlerJob),
            },
            message: React.createElement(
                isSuccess
                    ? eventRegisteredPopupProperties.completeComponent
                    : eventRegisteredPopupProperties.failedComponent,
                {
                    jobDetails: eventJobTyped.jobDetails,
                    result: eventJobTyped.result
                }
            ),
        });
    }

    private setCombinedAlertState(eventJobId: number | null, meetingJobId: number | null, isSuccess: boolean): void {
        this.shownAlertsNumber++;
        if (isSuccess) {
            this.combinedEventAlertShown = true;
        } else {
            this.combinedEventFailureShown = true;
        }
        this.eventBlueprintJobId = eventJobId;
        this.meetingBlueprintJobId = meetingJobId;
    }
}

export { JobsMonitorHostVM, IConcreteJobConfirmationPopupProperties, IJobResultComponentProps };
